// Spec 65 i18n-framework — ICU MessageFormat 2.0 formatter wrapper.
//
// What this module IS:
//   * A small interpolation layer that resolves `{varName}` placeholders
//     against a runtime values map.
//   * Optional `{varName, plural, ...}` and `{varName, select, ...}`
//     handling for the most common MF2 patterns. Uses Intl.PluralRules
//     for the plural side so CLDR rules apply per locale (Turkish has
//     1 plural form, English has 2, Polish has 3 — Intl.PluralRules
//     returns the right key).
//   * Strict — unknown placeholders left as-is is wrong (hides a bug).
//     We throw `MissingPlaceholderValueError` so the caller surfaces it.
//
// What this module is NOT:
//   * A full MF2 parser. Date/number/datetime/list selects + custom
//     functions ship in Phase 4b/5 by swapping the engine for
//     @messageformat/runtime or @formatjs/icu-messageformat-parser
//     (interface stays).
//   * A fallback engine — when a key is missing entirely, that's a
//     bundle problem, not the formatter's. Caller (i18next adapter,
//     Phase 4a.3c) raises that.
//
// Why we wrote a tiny one for Phase 4a:
//   * Avoid pulling in a 50KB dep before component library lands —
//     we don't yet know which parser features we lean on.
//   * The narrow surface (vars + plural + select) covers ~90% of MVP
//     bundle entries. The remaining 10% land in Phase 4b with the
//     real engine.

import type { LocaleCode } from "./locales.ts";

// ---------------------------------------------------------------------------
// Money formatting (CLAUDE.md money invariant; PHASE_0 B0 deliverable 4)
// ---------------------------------------------------------------------------
//
// The API edge and storage carry money as `<name>_minor_units` (integer) +
// a `currency` field (ISO 4217). This is the ONE place number, bigint, or
// bigint-as-string minor units reassemble into a locale-formatted string for
// display. Callers may include the currency symbol/code or request only the
// localized number; storage/compute never see a float.
//
// Zero-decimal currencies (JPY, KRW, …) carry NO minor digits — 1 yen = 1
// unit, not 100. We don't hardcode that table: Intl.NumberFormat already
// knows each currency's `maximumFractionDigits`, so string placement scales
// minor units exactly without floating-point division. 100 kuruş = 1 TL,
// 100 cents = 1 USD, 1 yen = 1 JPY all come out right.

export class InvalidMoneyError extends Error {
  constructor(reason: string) {
    super(`formatMoney: ${reason}`);
    this.name = "InvalidMoneyError";
  }
}

/**
 * Number of minor-unit digits a currency uses, per the locale's Intl data.
 * USD/TRY → 2, JPY/KRW → 0, BHD/KWD → 3. Memoized per currency since the
 * lookup constructs a formatter.
 */
const minorDigitsCache = new Map<string, number>();

function currencyMinorDigits(currency: string): number {
  const cached = minorDigitsCache.get(currency);
  if (cached !== undefined) return cached;
  // resolvedOptions().maximumFractionDigits reflects the currency's standard
  // minor-unit count. Constructed in a fixed locale — the digit count is a
  // property of the currency, not the display locale. The Intl typings make
  // maximumFractionDigits optional; for a currency-style formatter it is
  // always present, so default to 2 (the common minor-unit count) if absent.
  const digits =
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).resolvedOptions().maximumFractionDigits ?? 2;
  minorDigitsCache.set(currency, digits);
  return digits;
}

export interface FormatMoneyOptions {
  readonly currencyDisplay?: "symbol" | "code" | "none";
}

/**
 * Format number, bigint, or bigint-as-string integer minor units + ISO-4217
 * currency into a locale-aware display string. `currencyDisplay` defaults to
 * `"symbol"`; `"none"` returns only the localized number.
 *
 *   formatMoney(125_000, "TRY", "tr-TR")  → "₺1.250,00"
 *   formatMoney(125_000, "TRY", "en-US")  → "TRY 1,250.00"
 *   formatMoney(1500,    "USD", "en-US")  → "$15.00"
 *   formatMoney(1500,    "JPY", "ja-JP")  → "￥1,500"  (zero-decimal)
 *
 * Throws InvalidMoneyError when `minorUnits` is not an integer (floats are a
 * contract violation — money is integer minor units) or `currency` isn't a
 * recognized 3-letter ISO-4217 code.
 */
export function formatMoney(
  minorUnits: number | bigint | string,
  currency: string,
  locale: LocaleCode,
  options?: FormatMoneyOptions,
): string {
  let normalizedMinorUnits: string;
  if (typeof minorUnits === "number") {
    if (!Number.isInteger(minorUnits)) {
      throw new InvalidMoneyError(
        `minorUnits must be an integer (got ${String(minorUnits)}) — money is integer minor units`,
      );
    }
    normalizedMinorUnits = BigInt(minorUnits).toString();
  } else if (typeof minorUnits === "bigint") {
    normalizedMinorUnits = minorUnits.toString();
  } else if (/^-?\d+$/.test(minorUnits)) {
    normalizedMinorUnits = minorUnits;
  } else {
    throw new InvalidMoneyError(
      `minorUnits must be an integer (got ${String(minorUnits)}) — money is integer minor units`,
    );
  }
  if (typeof currency !== "string" || !/^[A-Za-z]{3}$/.test(currency)) {
    throw new InvalidMoneyError(`currency must be a 3-letter ISO-4217 code (got "${currency}")`);
  }
  const upperCurrency = currency.toUpperCase();
  let digits: number;
  try {
    digits = currencyMinorDigits(upperCurrency);
  } catch {
    throw new InvalidMoneyError(`unrecognized ISO-4217 currency "${currency}"`);
  }

  const negative = normalizedMinorUnits.startsWith("-");
  const magnitude = negative ? normalizedMinorUnits.slice(1) : normalizedMinorUnits;
  const paddedMagnitude = magnitude.padStart(digits + 1, "0");
  const splitAt = paddedMagnitude.length - digits;
  const majorUnits = digits === 0
    ? `${negative ? "-" : ""}${paddedMagnitude}`
    : `${negative ? "-" : ""}${paddedMagnitude.slice(0, splitAt)}.${paddedMagnitude.slice(splitAt)}`;
  const currencyDisplay = options?.currencyDisplay ?? "symbol";
  const formatter = currencyDisplay === "none"
    ? new Intl.NumberFormat(locale, {
        style: "decimal",
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })
    : new Intl.NumberFormat(locale, {
        style: "currency",
        currency: upperCurrency,
        currencyDisplay,
      });

  // NumberFormat V3 preserves arbitrary-precision decimal strings; TypeScript's
  // Intl declaration still omits string, so this cast changes only the static type.
  return formatter.format(majorUnits as unknown as number);
}

export class MissingPlaceholderValueError extends Error {
  readonly placeholder: string;
  constructor(placeholder: string) {
    super(`format: placeholder "${placeholder}" had no value`);
    this.name = "MissingPlaceholderValueError";
    this.placeholder = placeholder;
  }
}

export class MalformedPatternError extends Error {
  constructor(pattern: string, reason: string) {
    super(`format: malformed pattern "${pattern}" — ${reason}`);
    this.name = "MalformedPatternError";
  }
}

export type FormatValues = Readonly<Record<string, string | number>>;

/**
 * Format an MF2 pattern against a values map for the given locale.
 * Throws MissingPlaceholderValueError when a placeholder has no value;
 * throws MalformedPatternError when the pattern can't be parsed.
 *
 * Supported patterns:
 *   "Hello, {name}!"
 *   "{count, plural, one {# message} other {# messages}}"
 *   "{role, select, admin {Admin} member {Member} other {Guest}}"
 *
 * Combined patterns (selectors inside literal text) work:
 *   "{name} sent {count, plural, one {# file} other {# files}}."
 */
export function format(
  pattern: string,
  values: FormatValues,
  locale: LocaleCode,
): string {
  if (typeof pattern !== "string") {
    throw new MalformedPatternError(String(pattern), "pattern must be a string");
  }
  if (pattern.length === 0) return pattern;

  return interpolate(pattern, values, locale);
}

// Walk the pattern char-by-char, splitting at `{...}` segments so
// nested selectors (which contain their own `{}` for literal results)
// parse correctly. We track brace depth to find matching `}`.
function interpolate(pattern: string, values: FormatValues, locale: LocaleCode): string {
  let out = "";
  let i = 0;
  while (i < pattern.length) {
    const ch = pattern[i];
    if (ch !== "{") {
      out += ch;
      i += 1;
      continue;
    }
    // Found `{` — find matching `}`.
    const end = findMatchingBrace(pattern, i);
    if (end < 0) {
      throw new MalformedPatternError(pattern, `unmatched '{' at offset ${i}`);
    }
    const innerRaw = pattern.slice(i + 1, end).trim();
    out += resolvePlaceholder(innerRaw, values, locale, pattern);
    i = end + 1;
  }
  return out;
}

function findMatchingBrace(s: string, openIdx: number): number {
  let depth = 1;
  for (let i = openIdx + 1; i < s.length; i += 1) {
    if (s[i] === "{") depth += 1;
    else if (s[i] === "}") {
      depth -= 1;
      if (depth === 0) return i;
    }
  }
  return -1;
}

function resolvePlaceholder(
  inner: string,
  values: FormatValues,
  locale: LocaleCode,
  fullPattern: string,
): string {
  // Plain `{name}` — look up + stringify.
  if (!inner.includes(",")) {
    const value = values[inner];
    if (value === undefined) {
      throw new MissingPlaceholderValueError(inner);
    }
    return String(value);
  }
  // `{name, kind, ...}` — split on the first two commas; the rest is
  // the body which contains the selector → literal mappings.
  const firstComma = inner.indexOf(",");
  const secondComma = inner.indexOf(",", firstComma + 1);
  if (secondComma < 0) {
    throw new MalformedPatternError(
      fullPattern,
      `selector form needs '{name, kind, body}', got "${inner}"`,
    );
  }
  const varName = inner.slice(0, firstComma).trim();
  const kind = inner.slice(firstComma + 1, secondComma).trim();
  const body = inner.slice(secondComma + 1).trim();
  const rawValue = values[varName];
  if (rawValue === undefined) {
    throw new MissingPlaceholderValueError(varName);
  }
  if (kind === "plural") {
    return resolvePlural(rawValue, body, values, locale, fullPattern);
  }
  if (kind === "select") {
    return resolveSelect(String(rawValue), body, values, locale, fullPattern);
  }
  throw new MalformedPatternError(fullPattern, `unknown selector kind "${kind}"`);
}

function resolvePlural(
  rawValue: string | number,
  body: string,
  values: FormatValues,
  locale: LocaleCode,
  fullPattern: string,
): string {
  const n = typeof rawValue === "number" ? rawValue : Number(rawValue);
  if (!Number.isFinite(n)) {
    throw new MalformedPatternError(fullPattern, `plural value not numeric: "${String(rawValue)}"`);
  }
  const cases = parseSelectorCases(body, fullPattern);
  // CLDR plural category for the locale + value.
  const category = new Intl.PluralRules(locale).select(n);
  const chosen = cases[category] ?? cases["other"];
  if (chosen === undefined) {
    throw new MalformedPatternError(fullPattern, `plural body missing 'other' case`);
  }
  // Replace `#` (if present) with the formatted number per locale.
  const numberFormatted = new Intl.NumberFormat(locale).format(n);
  const expanded = chosen.replace(/#/g, numberFormatted);
  return interpolate(expanded, values, locale);
}

function resolveSelect(
  rawValue: string,
  body: string,
  values: FormatValues,
  locale: LocaleCode,
  fullPattern: string,
): string {
  const cases = parseSelectorCases(body, fullPattern);
  const chosen = cases[rawValue] ?? cases["other"];
  if (chosen === undefined) {
    throw new MalformedPatternError(fullPattern, `select body missing 'other' case`);
  }
  return interpolate(chosen, values, locale);
}

/**
 * Parse the body of a plural/select form into a {caseName: literalText} map.
 * Body shape: "one {a} other {b}" or "admin {Admin} other {Guest}".
 * We track brace depth to allow nested patterns inside the literals.
 */
function parseSelectorCases(body: string, fullPattern: string): Record<string, string> {
  const cases: Record<string, string> = {};
  let i = 0;
  while (i < body.length) {
    // Skip whitespace.
    while (i < body.length && /\s/.test(body[i] ?? "")) i += 1;
    if (i >= body.length) break;
    // Read case name (alphanumeric / _ / =N for explicit numeric cases).
    const nameStart = i;
    while (i < body.length && /[a-zA-Z0-9_=]/.test(body[i] ?? "")) i += 1;
    const name = body.slice(nameStart, i);
    if (name.length === 0) {
      throw new MalformedPatternError(fullPattern, `empty case name at offset ${nameStart}`);
    }
    // Skip whitespace.
    while (i < body.length && /\s/.test(body[i] ?? "")) i += 1;
    // Expect `{`.
    if (body[i] !== "{") {
      throw new MalformedPatternError(
        fullPattern,
        `expected '{' after case name "${name}" at offset ${i}`,
      );
    }
    const end = findMatchingBrace(body, i);
    if (end < 0) {
      throw new MalformedPatternError(fullPattern, `unmatched '{' in case "${name}"`);
    }
    cases[name] = body.slice(i + 1, end);
    i = end + 1;
  }
  return cases;
}
